import { useState } from "react";
import { useSelector } from "react-redux";

import {
  useCreateCompanyMutation,
  useCreateInsuranceMutation,
  useCreateInsuranceDraftMutation,
  useUpdateInsuranceMutation,
  useUpdateInsuranceDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { isVerified } from "../../../../utils/Helper";
import { CommonToast } from "../../../common/Toast";
import useCompanyFollowUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import {
  INSURANCE_SPEC,
  editingInsurance,
  needsCompanyRecord,
  validateInsurance,
} from "./insuranceFields";

/* Add / edit an insurance record. */
export const useInsuranceForm = ({ editing, onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const seed = editing ? editingInsurance(editing) : null;

  const [createCompany] = useCreateCompanyMutation();
  const [createInsurance] = useCreateInsuranceMutation();
  const [createInsuranceDraft] = useCreateInsuranceDraftMutation();
  const [updateInsurance] = useUpdateInsuranceMutation();
  const [updateInsuranceDraft] = useUpdateInsuranceDraftMutation();
  const followUpdate = useCompanyFollowUpdate();

  const [type, setType] = useState(seed?.type || "");
  /* The chosen businesses, each either a search result or a typed-in name.
     Seeded from the record as a plain name, which is all an existing row
     stores for them. */
  const [insurer, setInsurer] = useState(
    seed?.insurer ? { name: seed.insurer, companyID: seed.insurerCompanyID } : null
  );
  const [broker, setBroker] = useState(
    seed?.broker ? { name: seed.broker, companyID: seed.brokerCompanyID } : null
  );
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const nameOf = (chosen) =>
    chosen?.registered_company_name || chosen?.name || "";

  /* Transcribed: created only when the business has a WorldBox id and no
     TREVEX one. A typed-in name has neither, so nothing is created for it and
     the record carries the name alone. */
  const ensureCompany = async (chosen) => {
    if (!chosen) return "";
    if (chosen.companyID) return chosen.companyID;
    if (!needsCompanyRecord(chosen)) return "";

    try {
      const res = await createCompany({
        data: {
          registered_company_name: nameOf(chosen),
          country_code: chosen.country || chosen.country_code,
          wb_win: chosen.win || chosen.wb_win,
        },
        registered_company_name: nameOf(chosen),
        country_code: chosen.country || chosen.country_code,
        token,
      });
      return res?.data?.data?.companyID || "";
    } catch {
      /* Transcribed: the live code swallows this and carries on with an empty
         id rather than abandoning the save. */
      return "";
    }
  };

  const submit = async () => {
    const found = validateInsurance({ type, insurer: nameOf(insurer) });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    try {
      const insurerCompanyID = (await ensureCompany(insurer)) || seed?.insurerCompanyID || "";
      const brokerCompanyID = (await ensureCompany(broker)) || seed?.brokerCompanyID || "";

      /* The fields every branch sends, however it sends them. */
      const core = {
        current_companyID: companyID,
        type,
        insurer: nameOf(insurer),
        broker: nameOf(broker),
      };
      /* Only the CREATE payloads carry these four — the live update branches
         send neither, so an edit does not move a business's location. */
      const places = {
        insurer_country: insurer?.country || insurer?.country_code,
        insurer_state: insurer?.state,
        broker_country: broker?.country || broker?.country_code,
        broker_state: broker?.state,
      };

      if (isVerified(currentEditingCompany?.verifiedCompany)) {
        if (editing?.[INSURANCE_SPEC.draftIdKey]) {
          /* Editing a draft that is already queued. It keeps its link to the
             live row when it has one; when it does not, it carries a status
             instead — transcribed, both branches. */
          const linkedId = editing?.[INSURANCE_SPEC.draftLinkKey];
          await updateInsuranceDraft({
            token,
            insuranceID: editing[INSURANCE_SPEC.draftIdKey],
            data: {
              ...core,
              request_type: "Create draft api calling",
              insurer_comp_ID: insurerCompanyID,
              broker_company_ID: brokerCompanyID,
              ...(linkedId != null
                ? { insuranceID: linkedId }
                : { status: "pending" }),
            },
          }).unwrap();
        } else if (editing) {
          /* Editing a LIVE row on a verified company: the change becomes a
             new draft that points back at the row it amends. */
          await createInsuranceDraft({
            token,
            data: {
              ...core,
              request_type: "Create draft api calling",
              insurer_comp_ID: insurerCompanyID,
              broker_company_ID: brokerCompanyID,
              status: "pending",
              insuranceID: editing[INSURANCE_SPEC.idKey],
            },
          }).unwrap();
        } else {
          await createInsuranceDraft({
            token,
            data: {
              ...core,
              ...places,
              /* Verbatim, including the trailing "k" — see F-069. */
              request_type: "Create draft api callingk",
              insurer_comp_ID: insurerCompanyID,
              broker_company_ID: brokerCompanyID,
              status: "pending",
            },
          }).unwrap();
        }

        CommonToast.success("Sent for review. The team confirms the change shortly.");
        onSaved?.();
        return;
      }

      if (editing) {
        await updateInsurance({
          token,
          insuranceID: editing[INSURANCE_SPEC.idKey],
          data: {
            ...core,
            insurer_company_ID: insurerCompanyID,
            broker_company_ID: brokerCompanyID,
          },
        }).unwrap();
        await followUpdate(companyID, INSURANCE_SPEC.followUpdated, token);
        CommonToast.success("Insurance updated.");
      } else {
        await createInsurance({
          token,
          data: {
            ...core,
            ...places,
            insurer_company_ID: insurerCompanyID,
            broker_company_ID: brokerCompanyID,
          },
        }).unwrap();
        await followUpdate(companyID, INSURANCE_SPEC.followCreated, token);
        CommonToast.success("Insurance added.");
      }

      onSaved?.();
    } catch (error) {
      /* Every branch of the live version has an empty catch, then closes the
         dialog regardless — so a rejected save looked like a saved one. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't save this insurance record. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return {
    type,
    setType,
    insurer,
    setInsurer,
    broker,
    setBroker,
    /* Transcribed: an existing row's insurer cannot be changed — the live
       input is `disabled={Boolean(insurer)}` once one is chosen. */
    insurerLocked: Boolean(seed?.insurer),
    errors,
    setErrors,
    busy,
    submit,
  };
};

export default useInsuranceForm;
